import * as angular from 'angular';
import { IController, IWindowService } from 'angular';
import { each, find, forIn, get, groupBy, isEmpty, keys as _keys, map, reduce, values as _values } from 'lodash';
import { ImSchool } from 'Src/ng2/shared/services/im-models/im-school';
import { CurrentSchoolYear } from '../../../ng2/shared/constants/current-school-year.constant';
import { RegentsSubject } from '../../../ng2/shared/constants/regents.constant';
import { SubjectAreas } from '../../../ng2/shared/constants/subject-areas.constant';
import { ICourse } from '../../../ng2/shared/typings/interfaces/school.interface';
import { SdcActionsService } from '../../sdc/flux/sdc-actions.service';
import { ISdcStore } from '../../sdc/flux/sdc-store.interface';
import { AssignPrepsForModal } from '../../shared/modals/assign-preps-for/assign-preps-for';

interface ICourseExtended extends ICourse {
  isRegentsCulminatingString: 'Yes' | 'No';
  prepCourses: {
    prep1: string;
    prep2: string;
  };
}

interface ISubjectAreas {
  [key: string]: string;
}

interface ICourseFilter {
  courseCodeWithSection: any;
  subjectArea: any;
  teachers: any;
  isRegentsCulminating: any;
  prepsFor: any;
}

export class MasterProgramCtrl implements IController {
  private atLeastOneCourseIsChecked: boolean;
  private courseFilter: ICourseFilter;
  private currentSchoolYear: string;
  private currentTermNumber: number;
  private currentTermYear: number;
  private examNames: string[];
  private humanTermYear: string;
  private masterProgram: [];
  private prepCourses;
  private selectedCourses: [];
  private subjectAreas: ISubjectAreas;

  // bindings
  private currentSchool: any;

  constructor(
    private $mdDialog,
    private $scope,
    private $window: IWindowService,
    private AssignPrepsForModal: AssignPrepsForModal,
    private ErrorModal,
    private ImSchool: ImSchool,
    private Rollbar,
    private SDCStore: ISdcStore,
    private SpinnerModal,
    private UtilitiesService,
    private sdcActions: SdcActionsService,
  ) {}

  $onInit() {
    this.SpinnerModal.deactivateSpinner();

    this.examNames = this.getExamNames();

    this.$scope.$on(
      'atLeastOneCourseIsChecked',
      (event, boolean): void => {
        // to disable "Assign Preps For" button
        this.atLeastOneCourseIsChecked = boolean;
      },
    );

    this.$window.currentSchool = this.currentSchool;
    this.currentTermNumber = this.ImSchool.getCurrentTermNumber(this.currentSchool);
    this.currentSchoolYear = `${CurrentSchoolYear.START}-${CurrentSchoolYear.END}`;
    // TODO: this is hack to get support end dates working for  'ANNUALIZED' schools
    // Further discussions required with data team to address issues pertaining to 'ANNUALIZED' schools `currentTermNumber` (CM)
    const termNumberToDisplay = this.getTermNumberToDisplay();
    this.humanTermYear = `Term ${termNumberToDisplay} SY${this.currentSchoolYear}`;
    this.currentTermYear = this.ImSchool.getCurrentTermYear(this.currentSchool);
    const masterProgramForCurrentTermYear = this.ImSchool.getMasterScheduleForTermYear(
      this.currentSchool,
      this.currentTermYear,
    );
    this.masterProgram = masterProgramForCurrentTermYear;

    this.subjectAreas = this.getSubjectAreas();

    this.courseFilter = {
      courseCodeWithSection: null,
      subjectArea: null,
      teachers: null,
      isRegentsCulminating: null,
      prepsFor: null,
    };

    this.selectedCourses = [];

    this.prepCourses = this.convertSchoolFormToPrepCourses(this.currentSchool);
  }

  private getExamNames(): string[] {
    return map(RegentsSubject, exam => {
      return exam.key;
    });
  }

  private getSubjectAreas() {
    return reduce(
      this.masterProgram,
      (result, course: any) => {
        const subjectArea = course.subjectArea;
        if (!result[subjectArea]) {
          const subjectAreaObj: any = find(SubjectAreas, { camelCase: subjectArea });
          result[subjectArea] = subjectAreaObj ? subjectAreaObj.human : subjectArea;
        }
        return result;
      },
      {},
    );
  }

  private getTermNumberToDisplay(): number {
    const condition1 = this.currentSchool.termStructure === 'ANNUALIZED';
    const condition2 = this.currentSchool.currentTermYear === 182;
    return condition1 && condition2 ? 1 : this.currentTermNumber;
  }

  public teachers(teachersArray: any[]): string {
    const nameArray = [];
    teachersArray.forEach(teacher => {
      nameArray.push(teacher.nickname);
    });
    return nameArray.join(', ');
  }

  private convertSchoolFormToPrepCourses(currentSchool) {
    const prepCoursesFormObject = {};
    const userEnteredPrepCourses = this.currentSchool.userEnteredPrepCourses; // array of objects
    const organizedUserData = groupBy(userEnteredPrepCourses, 'courseId');
    each(this.masterProgram, (course: ICourseExtended, key) => {
      const courseId = course.courseId;
      if (courseId) {
        prepCoursesFormObject[courseId] = {
          prep1: '',
          prep2: '',
        };

        if (organizedUserData[courseId]) {
          const courses: any = organizedUserData[courseId];

          for (let i = 0; i < courses.length; i++) {
            prepCoursesFormObject[courseId]['prep' + (i + 1)] = courses[i].coursePrepsFor;
          }
        }
      }

      if (course.isRegentsCulminating === true) {
        course.isRegentsCulminatingString = 'Yes';
      } else {
        course.isRegentsCulminatingString = 'No';
      }
    });
    return prepCoursesFormObject;
  }

  private onPrepCoursesChange(): void {
    // We create a copy of `this.prepCourses` so that we can assing a new reference to `this.prepCourses`.
    // The reassignment of `this.prepCourses` is done at the end of this method
    // to trigger the $onChanges lyfecycle hook in child components. - CM
    const prepCourses = Object.assign({}, this.prepCourses);

    each(prepCourses, (courseValue, courseId) => {
      if (!isEmpty(courseValue)) {
        each(courseValue, (prepValue, prepId) => {
          if (prepValue === '') {
            delete this.prepCourses[courseId][prepId];
            if (isEmpty(courseValue)) {
              delete this.prepCourses[courseId];
            }
          }
        });
        this.currentSchool.userEnteredPrepCourses = this.convertPrepCoursesToSchoolForm(this.prepCourses);
      } else {
        this.currentSchool.userEnteredPrepCourses = this.convertPrepCoursesToSchoolForm(this.prepCourses);
      }
    });

    // Assign new reference for `this.prepCourses`.
    // This reassignment will trigger the $onChanges lyfecycle hook in child components. -CM
    this.prepCourses = prepCourses;
  }

  private save(): void {
    const path = 'userEnteredPrepCourses';
    this.SpinnerModal.activateSpinner('Updating School');
    this.onPrepCoursesChange();
    this.currentSchool.$patchFields(
      [path],
      (cs, headers) => {
        const patch = get(cs, path);
        this.sdcActions.updateSchoolByPatch(angular.copy(patch));
        this.SpinnerModal.deactivateSpinner();
      },
      error => {
        this.ErrorModal.show('Unable to save your changes', error);
      },
    );
  }

  public assignPrepsForModal(): void {
    this.AssignPrepsForModal.show(this.examNames, this.selectedCourses)
      .then(({ prep1, prep2 }) => {
        if (prep1 || prep2) {
          each(this.selectedCourses, (course: ICourseExtended) => {
            if (!course.prepCourses) {
              course.prepCourses = {
                prep1: null,
                prep2: null,
              };
            }

            const { prepCourses } = course;
            const { prep1ToAssign, prep2ToAssign } = this.getUserEnteredPrepsForToAssign({
              existingPrepCourses: prepCourses,
              selectedPrep1: prep1,
              selectedPrep2: prep2,
            });

            course.prepCourses.prep1 = prep1ToAssign;
            course.prepCourses.prep2 = prep2ToAssign;

            if (!this.prepCourses[course.courseId]) {
              this.prepCourses[course.courseId] = this.UtilitiesService.copyPOJO(course.prepCourses);
            }
          });

          this.save();
        }
      })
      .catch(err => {
        // err is undefined if user cancels modal (JE)
        if (err) this.Rollbar.error(`Unable to set preps for selected courses`, err);
      });
  }

  // gets user entered "preps for" to assign to a course -CM
  private getUserEnteredPrepsForToAssign({ existingPrepCourses, selectedPrep1, selectedPrep2 }) {
    const { prep1: existingPrep1, prep2: existingPrep2 } = existingPrepCourses;
    let prep1ToAssign;
    let prep2ToAssign;

    // 'None' option has been selected, so assign `''`. -CM
    if (selectedPrep1 && selectedPrep1.toLowerCase() === 'none') prep1ToAssign = '';
    // assign selected option, current value, or, if neither exists, assign `''`. -CM
    else prep1ToAssign = selectedPrep1 || existingPrep1 || '';

    // 'None' option has been selected, so assign `''`. -CM
    if (selectedPrep2 && selectedPrep2.toLowerCase() === 'none') prep2ToAssign = '';
    // assign selected option, current value, or, if neither exists, assign `''`. -CM
    else prep2ToAssign = selectedPrep2 || existingPrep2 || '';

    // Assign the value of `prep2` to `prep1` if `prep2` has a value and `prep1` does not. -CM
    if (!prep1ToAssign && prep2ToAssign) {
      prep1ToAssign = prep2ToAssign;
      prep2ToAssign = '';
    }

    return { prep1ToAssign, prep2ToAssign };
  }

  private convertPrepCoursesToSchoolForm(prepCourse) {
    const unfilteredPrepArray = [];
    const filteredPrepArray = [];

    forIn(prepCourse, (value, key) => {
      const keys = _keys(value);
      const values = _values(value);

      for (let i = 0; i < keys.length; i++) {
        const tempObj: any = {};
        tempObj.courseId = key;
        tempObj.coursePrepsFor = values[i];
        unfilteredPrepArray.push(tempObj);
      }
    });

    unfilteredPrepArray.forEach((value, i) => {
      if (value.coursePrepsFor !== '') {
        filteredPrepArray.push(value);
      }
    });

    return filteredPrepArray;
  }

  public updateCourseFilter(newFilter) {
    this.courseFilter = newFilter;
    this.$scope.courseFilter = newFilter;
  }
}
angular.module('nv.SchoolMetricsModule').controller('MasterProgramCtrl', MasterProgramCtrl);
